/* Street Lines — "prolistovat" preview lightbox.
 *
 * Opened by any [data-sl-peek-open] control. Swipe on touch, arrows / Esc /
 * backdrop on desktop. Focus is trapped while open and restored on close.
 *
 * The images are deliberately small, compressed copies, painted as CSS
 * backgrounds under a transparent shield, and the markup adds the usual
 * deterrents (no context menu, no drag, no selection, no touch callout). That
 * raises the effort of grabbing a page; it cannot make a displayed image
 * unobtainable, and is not meant to.
 */
if (!customElements.get('sl-peek')) {
  customElements.define(
    'sl-peek',
    class SlPeek extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('[data-sl-peek-dialog]');
        this.track = this.querySelector('[data-sl-peek-track]');
        this.slides = [...this.querySelectorAll('[data-sl-peek-slide]')];
        this.counter = this.querySelector('[data-sl-peek-counter]');
        this.prevBtn = this.querySelector('[data-sl-peek-prev]');
        this.nextBtn = this.querySelector('[data-sl-peek-next]');
        this.closeBtns = this.querySelectorAll('[data-sl-peek-close]');
        this.lastFocus = null;
        this.index = 0;

        if (!this.dialog || !this.slides.length) return;

        document.addEventListener('click', (e) => {
          const trigger = e.target.closest('[data-sl-peek-open]');
          if (!trigger) return;
          e.preventDefault();
          this.open(trigger);
        });

        // Start fetching the pages the moment someone reaches for a trigger,
        // so they are usually in by the time the click lands.
        const armOnIntent = (e) => {
          if (e.target.closest && e.target.closest('[data-sl-peek-open]')) this.arm();
        };
        ['pointerover', 'touchstart', 'focusin'].forEach((type) =>
          document.addEventListener(type, armOnIntent, { passive: true })
        );

        this.closeBtns.forEach((b) => b.addEventListener('click', () => this.close()));
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.go(this.index - 1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.go(this.index + 1));

        // Keep the counter honest when the strip is swiped rather than clicked.
        if (this.track) {
          let t;
          this.track.addEventListener(
            'scroll',
            () => {
              clearTimeout(t);
              t = setTimeout(() => {
                const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
                this.index = Math.max(0, Math.min(this.slides.length - 1, i));
                this.sync();
              }, 80);
            },
            { passive: true }
          );
        }

        // Deterrents, scoped to the preview only: no context menu anywhere in
        // the lightbox (the pages are CSS backgrounds under a shield), no drag.
        this.addEventListener('contextmenu', (e) => e.preventDefault());
        this.addEventListener('dragstart', (e) => e.preventDefault());
        this.addEventListener('selectstart', (e) => e.preventDefault());

        this.setAttribute('inert', '');
      }

      arm() {
        this.classList.add('is-armed');
      }

      open(trigger) {
        this.arm();
        this.lastFocus = trigger || document.activeElement;
        this.removeAttribute('inert');
        this.classList.add('is-open');
        document.documentElement.classList.add('sl-no-scroll');
        this.go(0, true);
        document.addEventListener('keydown', this.onKey);
        const first = this.querySelector('[data-sl-peek-close]');
        if (first) first.focus();
      }

      close() {
        this.classList.remove('is-open');
        document.documentElement.classList.remove('sl-no-scroll');
        document.removeEventListener('keydown', this.onKey);
        this.setAttribute('inert', '');
        if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
      }

      onKey = (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.close();
        } else if (e.key === 'ArrowRight') {
          this.go(this.index + 1);
        } else if (e.key === 'ArrowLeft') {
          this.go(this.index - 1);
        } else if (e.key === 'Tab') {
          const items = [...this.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])')].filter(
            (el) => el.offsetParent !== null
          );
          if (!items.length) return;
          const first = items[0];
          const last = items[items.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };

      go(i, instant) {
        this.index = Math.max(0, Math.min(this.slides.length - 1, i));
        if (this.track) {
          this.track.scrollTo({
            left: this.index * this.track.clientWidth,
            behavior: instant ? 'auto' : 'smooth',
          });
        }
        this.sync();
      }

      sync() {
        if (this.counter) this.counter.textContent = `${this.index + 1} / ${this.slides.length}`;
        if (this.prevBtn) this.prevBtn.disabled = this.index === 0;
        if (this.nextBtn) this.nextBtn.disabled = this.index === this.slides.length - 1;
      }
    }
  );
}
